/**
 * Network Redux Slice
 *
 * Manages network connectivity state in Redux store.
 *
 * Requirements: 5.4
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * Network state
 */
export interface NetworkState {
    /** Whether the app is currently online */
    readonly isOnline: boolean

    /** Number of queued operations */
    readonly queuedOperations: number

    /** Timestamp of last status change */
    readonly lastStatusChange: number | null
}

/**
 * Initial state
 */
const initialState: NetworkState = {
    isOnline: navigator.onLine,
    queuedOperations: 0,
    lastStatusChange: null,
}

/**
 * Network slice
 */
const networkSlice = createSlice({
    name: 'network',
    initialState,
    reducers: {
        /**
         * Set online status
         */
        setOnlineStatus(state, action: PayloadAction<boolean>) {
            state.isOnline = action.payload
            state.lastStatusChange = Date.now()
        },

        /**
         * Set queued operations count
         */
        setQueuedOperations(state, action: PayloadAction<number>) {
            state.queuedOperations = action.payload
        },

        /**
         * Increment queued operations
         */
        incrementQueuedOperations(state) {
            state.queuedOperations += 1
        },

        /**
         * Decrement queued operations
         */
        decrementQueuedOperations(state) {
            state.queuedOperations = Math.max(0, state.queuedOperations - 1)
        },

        /**
         * Reset network state
         */
        resetNetworkState(state) {
            state.isOnline = navigator.onLine
            state.queuedOperations = 0
            state.lastStatusChange = null
        },
    },
})

export const {
    setOnlineStatus,
    setQueuedOperations,
    incrementQueuedOperations,
    decrementQueuedOperations,
    resetNetworkState,
} = networkSlice.actions

export const networkReducer = networkSlice.reducer
